import { useCallback, useEffect, useState } from 'react';
import { supabase } from '../../../lib/supabaseClient';
import { toFa } from '../../../utils/faFormat';
import { AvaMedia, extractMedia, formatTime, SESSION_KEY } from './AvaChat';
import '../../../styles/AvaChat.css';
import '../../../styles/AvaHistory.css';

// Browse + read + resume past «آوا» conversations. Talks ONLY to our own
// admin-gated /api/ava/* reverse-proxy (server/routes/ava.js); the upstream
// hermes api_server + AVA_API_KEY are never visible here. Two views: a paginated
// list of session cards, and an inline read-only transcript reusing AvaChat's
// AvaMedia/extractMedia/formatTime rendering primitives. Resuming writes the
// session id to localStorage (the key AvaChat reads) and calls onResume so the
// parent tab switches back to the live chat.

const PAGE_SIZE = 30;

// Read the current Supabase access token to send as the admin Bearer. Admin
// gating happens server-side; we just need a token. Mirrors AvaChat.
async function getAccessToken() {
  const { data } = await supabase.auth.getSession();
  return data?.session?.access_token || null;
}

// Persian Jalali date + short time for a session's last-active moment. Upstream
// timestamps are epoch SECONDS (float), so scale to ms. fa-IR renders the Jalali
// calendar with Persian digits natively. Tolerant of a missing value.
function formatSessionTime(sec) {
  if (!sec) return '';
  try {
    return new Date(sec * 1000).toLocaleString('fa-IR', {
      year: 'numeric', month: 'short', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch (_) {
    return '';
  }
}

// A user turn's text may carry `[تصویر]` markers (the server flattens image
// content parts to that literal). Pull them out so we can show a small
// attachment chip per image and render the remaining text as a normal bubble.
function splitUserContent(content) {
  const marker = '[تصویر]';
  let images = 0;
  let text = String(content || '');
  while (text.includes(marker)) { images += 1; text = text.replace(marker, ''); }
  text = text.replace(/[ \t]+\n/g, '\n').replace(/\n{3,}/g, '\n\n').trim();
  return { images, text };
}

// One read-only transcript bubble, reusing the AvaChat visual language. User
// turns render image markers as attachment chips; assistant turns run through
// extractMedia so any MEDIA file tag renders with the shared <AvaMedia>.
function TranscriptBubble({ message }) {
  const isUser = message.role === 'user';
  const time = message.timestamp ? formatTime(message.timestamp * 1000) : '';
  return (
    <div className={`ava-msg ava-msg-${isUser ? 'user' : 'assistant'}`}>
      <div className="ava-msg-head">
        <span className="ava-msg-role">{isUser ? 'شما' : 'آوا'}</span>
        {time && <span className="ava-msg-time">{time}</span>}
      </div>
      {isUser ? <UserBody content={message.content} /> : <AssistantBody content={message.content} />}
    </div>
  );
}

function UserBody({ content }) {
  const { images, text } = splitUserContent(content);
  return (
    <>
      {images > 0 && (
        <div className="ava-history-chips">
          {Array.from({ length: images }).map((_, j) => (
            <span key={j} className="ava-history-chip">
              <svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
                <rect x="3" y="3" width="18" height="18" rx="2" />
                <circle cx="9" cy="9" r="1.6" />
                <path d="m21 15-5-5L5 21" />
              </svg>
              تصویر
            </span>
          ))}
        </div>
      )}
      {text && <div className="ava-msg-content">{text}</div>}
    </>
  );
}

function AssistantBody({ content }) {
  const { clean, media } = extractMedia(content);
  return (
    <>
      {clean && <div className="ava-msg-content">{clean}</div>}
      {media.length > 0 && (
        <div className="ava-msg-media">
          {media.map((md, j) => <AvaMedia key={md.path || j} item={md} />)}
        </div>
      )}
    </>
  );
}

export default function AvaHistory({ onResume }) {
  const [sessions, setSessions] = useState([]);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState('');

  // Transcript view: the open session id (null = list view) + its messages.
  const [openId, setOpenId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [transcriptLoading, setTranscriptLoading] = useState(false);
  const [transcriptError, setTranscriptError] = useState('');

  // Fetch one page. offset===0 is a fresh load (replaces the list + shows the
  // full-view loader); a positive offset appends (the «بیشتر» spinner).
  const loadPage = useCallback(async (offset) => {
    const append = offset > 0;
    if (append) setLoadingMore(true); else { setLoading(true); }
    setError('');
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('برای مشاهدهٔ تاریخچه باید وارد شوید.');
      const res = await fetch(`/api/ava/sessions?limit=${PAGE_SIZE}&offset=${offset}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'دریافت فهرست گفتگوها ناموفق بود.');
      const rows = Array.isArray(body.sessions) ? body.sessions : [];
      // Dedupe by id on append: a session created between two pages shifts the
      // upstream offsets, and a repeated row would collide as a React key.
      setSessions((prev) => (append
        ? [...prev, ...rows.filter((r) => !prev.some((p) => p.id === r.id))]
        : rows));
      setHasMore(!!body.has_more);
    } catch (e) {
      setError(e.message || 'خطایی رخ داد.');
    } finally {
      if (append) setLoadingMore(false); else setLoading(false);
    }
  }, []);

  useEffect(() => { loadPage(0); }, [loadPage]);

  // Open a session's transcript (id URL-encoded). A 502/404 shows a compact
  // Persian error with retry; the list state is untouched so «بازگشت» restores it.
  const openSession = useCallback(async (id) => {
    setOpenId(id);
    setMessages([]);
    setTranscriptError('');
    setTranscriptLoading(true);
    try {
      const token = await getAccessToken();
      if (!token) throw new Error('برای مشاهدهٔ گفتگو باید وارد شوید.');
      const res = await fetch(`/api/ava/sessions/${encodeURIComponent(id)}/messages`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(body.error || 'دریافت پیام‌های گفتگو ناموفق بود.');
      setMessages(Array.isArray(body.messages) ? body.messages : []);
    } catch (e) {
      setTranscriptError(e.message || 'خطایی رخ داد.');
    } finally {
      setTranscriptLoading(false);
    }
  }, []);

  // Resume the open session in the live chat: write the id where AvaChat reads
  // it, then let the parent tab remount + switch to «گفتگو».
  const resume = useCallback(() => {
    if (!openId) return;
    localStorage.setItem(SESSION_KEY, openId);
    if (typeof onResume === 'function') onResume(openId);
  }, [openId, onResume]);

  // ── Transcript view ──────────────────────────────────────────────────────
  if (openId !== null) {
    return (
      <div className="ava-history" dir="rtl">
        <div className="ava-history-bar">
          <button type="button" className="ava-history-back" onClick={() => setOpenId(null)}>
            <svg viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" focusable="false">
              <path d="M9 18l6-6-6-6" />
            </svg>
            بازگشت
          </button>
          <button type="button" className="ava-history-resume" onClick={resume} disabled={transcriptLoading || !!transcriptError}>
            ادامه این گفتگو
          </button>
        </div>
        {transcriptLoading ? (
          <div className="ava-history-status"><span className="ava-typing" aria-hidden="true"><i /><i /><i /></span> در حال بارگذاری گفتگو...</div>
        ) : transcriptError ? (
          <div className="ava-history-error" role="alert">
            <span>{transcriptError}</span>
            <button type="button" className="ava-history-retry" onClick={() => openSession(openId)}>تلاش دوباره</button>
          </div>
        ) : messages.length === 0 ? (
          <div className="ava-history-status">این گفتگو پیامی برای نمایش ندارد.</div>
        ) : (
          <div className="ava-transcript">
            {messages.map((m, i) => <TranscriptBubble key={i} message={m} />)}
          </div>
        )}
      </div>
    );
  }

  // ── List view ────────────────────────────────────────────────────────────
  return (
    <div className="ava-history" dir="rtl">
      <div className="ava-history-scroll">
        {loading ? (
          <div className="ava-history-skeletons" aria-hidden="true">
            {[0, 1, 2, 3].map((k) => <div key={k} className="ava-history-skeleton" />)}
          </div>
        ) : error && sessions.length === 0 ? (
          <div className="ava-history-error ava-history-error-full" role="alert">
            <span>{error}</span>
            <button type="button" className="ava-history-retry" onClick={() => loadPage(0)}>تلاش دوباره</button>
          </div>
        ) : sessions.length === 0 ? (
          <div className="ava-history-empty">هنوز گفتگویی ثبت نشده است.</div>
        ) : (
          <>
            <ul className="ava-history-list">
              {sessions.map((s) => (
                <li key={s.id}>
                  <button type="button" className="ava-history-card" onClick={() => openSession(s.id)}>
                    <div className="ava-history-preview">{s.preview || 'بدون پیش‌نمایش'}</div>
                    <div className="ava-history-meta">
                      <span className="ava-history-date">{formatSessionTime(s.last_active)}</span>
                      <span className="ava-history-count">{toFa(s.message_count ?? 0)} پیام</span>
                      {s.tool_call_count > 0 && (
                        <span className="ava-history-count">{toFa(s.tool_call_count)} ابزار</span>
                      )}
                      {/* No model chip: every Ava chat runs the config.yaml default
                          (hermes stamps such sessions "hermes-agent", the harness
                          name), so a per-row model label carries no information. */}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
            {/* A load-more failure keeps the existing list; the compact error sits
                above the button, which itself retries the same page. */}
            {error && <div className="ava-history-error" role="alert">{error}</div>}
            {hasMore && (
              <button
                type="button"
                className="ava-history-more"
                onClick={() => loadPage(sessions.length)}
                disabled={loadingMore}
              >
                {loadingMore ? 'در حال بارگذاری...' : 'بیشتر'}
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
}
